import React, { useContext, createContext, useEffect, useState } from "react";
import { PublicClientApplication, AuthenticationResult, InteractionRequiredAuthError } from "@azure/msal-browser";

import { LoggingContext } from "./LoggingContext";
import { FatalError } from "../services/ErrorHelpers";
import { config } from "../config";

export enum AuthenticationState {
  Authenticated = "Authenticated",
  Unauthenticated = "Unauthenticated",
  InProgress = "InProgress"
}

export interface IUserProviderState {
  user: {
    name: string;
    userName: string;
    accountIdentifier: string;
  };
  login: () => Promise<void>;
  logout: () => Promise<void>;
  accessToken: () => Promise<AuthenticationResult | undefined>;
  status: AuthenticationState;
}

const initialState: IUserProviderState = {
  user: {
    name: "",
    userName: "",
    accountIdentifier: ""
  },
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  accessToken: () => Promise.resolve(undefined),
  status: AuthenticationState.Unauthenticated
};
const userContext = createContext(initialState);
const { Provider } = userContext;

interface IUserProviderProps {
  children: React.ReactNode;
  authProvider: PublicClientApplication | null;
  mock: boolean;
}

const UserProvider = (props: IUserProviderProps): JSX.Element => {
  const { appInsights } = useContext(LoggingContext);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(initialState.user);
  const [isLoading, setIsLoading] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);

  if (!appInsights) {
    throw new FatalError("Setup an ApplicationInsights Provider.");
  }

  const scopes = [`${config.get("clientId")}/.default`]; // Use the same scope pattern as JEM General Ledger

  useEffect(() => {
    const initializeAuth = async () => {
      if (!props.authProvider || props.mock) {
        console.log("UserContext: Skipping auth - no provider or mock mode");
        setIsLoading(false);
        return;
      }

      try {
        console.log("UserContext: Starting authentication initialization");
        
        // Handle redirect response if any
        const response = await props.authProvider.handleRedirectPromise();
        console.log("UserContext: Redirect response:", response);
        
        let account = null;
        if (response) {
          account = response.account;
          console.log("UserContext: Account from redirect response:", account);
        } else {
          // Check for existing accounts
          const accounts = props.authProvider.getAllAccounts();
          console.log("UserContext: Existing accounts:", accounts);
          if (accounts.length > 0) {
            account = accounts[0];
            props.authProvider.setActiveAccount(account);
            console.log("UserContext: Set active account:", account);
          }
        }

        if (account) {
          console.log("UserContext: User authenticated:", account.name);
          setUser({
            name: account.name || "",
            userName: account.username || "",
            accountIdentifier: account.homeAccountId || ""
          });
          setIsAuthenticated(true);
        } else {
          console.log("UserContext: No account found, will need login");
          setNeedsLogin(true);
        }
      } catch (error) {
        console.error("UserContext: Authentication initialization failed:", error);
        appInsights.trackException({ exception: error as Error });
        setNeedsLogin(true);
      }
      
      setIsLoading(false);
      console.log("UserContext: Initialization complete");
    };

    initializeAuth();
  }, [props.authProvider, props.mock, appInsights]);

  // Separate effect to handle login trigger after initialization
  useEffect(() => {
    if (needsLogin && !isLoading && props.authProvider) {
      console.log("UserContext: Triggering automatic login");
      const triggerLogin = async () => {
        try {
          await props.authProvider!.loginRedirect({ 
            scopes,
            prompt: "select_account",
            extraQueryParameters: {
              response_mode: "fragment" // Explicit SPA response mode
            }
          });
        } catch (error) {
          console.error("UserContext: Automatic login failed:", error);
          appInsights.trackException({ exception: error as Error });
        }
      };
      triggerLogin();
    }
  }, [needsLogin, isLoading, props.authProvider, scopes, appInsights]);

  const login = async () => {
    if (!props.authProvider) return;
    try {
      await props.authProvider.loginRedirect({ 
        scopes,
        prompt: "select_account",
        extraQueryParameters: {
          response_mode: "fragment" // Explicit SPA response mode
        }
      });
    } catch (error) {
      console.error("Login failed:", error);
      appInsights.trackException({ exception: error as Error });
    }
  };

  const logout = async () => {
    if (!props.authProvider) return;
    try {
      await props.authProvider.logoutRedirect();
    } catch (error) {
      console.error("Logout failed:", error);
      appInsights.trackException({ exception: error as Error });
    }
  };

  const getToken = async (): Promise<AuthenticationResult | undefined> => {
    if (!props.authProvider) return undefined;
    
    try {
      const accounts = props.authProvider.getAllAccounts();
      if (accounts.length === 0) return undefined;

      const request = {
        scopes,
        account: accounts[0]
      };

      const response = await props.authProvider.acquireTokenSilent(request);
      return response;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          await props.authProvider.acquireTokenRedirect({
            scopes,
            account: props.authProvider.getAllAccounts()[0]
          });
        } catch (redirectError) {
          console.error("Token acquisition failed:", redirectError);
          appInsights.trackException({ exception: redirectError as Error });
        }
      }
      return undefined;
    }
  };

  if (props.mock) {
    return (
      <Provider
        value={{
          user: {
            name: "No User",
            userName: "NA",
            accountIdentifier: "NA"
          },
          login: () => Promise.resolve(),
          logout: () => Promise.resolve(),
          accessToken: () => Promise.resolve({
            accessToken: "mock-access-token",
            account: null,
            authority: "",
            cloudGraphHostName: "",
            correlationId: "",
            expiresOn: new Date(),
            extExpiresOn: new Date(),
            familyId: "",
            fromCache: false,
            fromNativeBroker: false,
            idToken: "",
            idTokenClaims: {},
            msGraphHost: "",
            scopes: [],
            state: "",
            tenantId: "",
            tokenType: "",
            uniqueId: ""
          } as AuthenticationResult),
          status: AuthenticationState.Authenticated
        }}
      >
        {props.children}
      </Provider>
    );
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (needsLogin && !isAuthenticated) {
    return <p>Redirecting to login...</p>;
  }

  if (isAuthenticated) {
    return (
      <Provider
        value={{
          user,
          login,
          logout,
          accessToken: getToken,
          status: AuthenticationState.Authenticated
        }}
      >
        {props.children}
      </Provider>
    );
  }

  // Fallback state
  return <p>Authentication required...</p>;
};

export { UserProvider, userContext as UserContext };
